<template>
  <main class="support-entry">
    <section v-if="!failed" class="support-entry__loading" role="status" aria-live="polite">
      <span class="support-entry__spinner" aria-hidden="true"></span>
      <p v-if="showDetails">{{ message }}</p>
    </section>
    <section v-else class="support-entry__error" role="alert">
      <feather-icon icon="AlertCircleIcon" svgClasses="w-8 h-8" />
      <h1>ไม่สามารถเปิดข้อมูลได้</h1>
      <p>{{ message }}</p>
    </section>
  </main>
</template>

<script>
import { GET_CURRENT_USER } from '@/queries/core'
import { GET_CONTRACTS_BY_CURRENT_USER } from '@/queries/modules/accounting'
import { supportState, startSupport, endSupport, supportActive, supportQuery, selectSupportContract, markSupportReady } from '@/utils/student-support-session'

export default {
  data () { return { message: 'กำลังเปิดข้อมูล…', showDetails: false, failed: false } },
  async mounted () {
    // Keep successful handoffs visually quiet; reveal text only when loading is perceptibly slow.
    this.detailsTimer = window.setTimeout(() => { this.showDetails = true }, 800)
    if (supportActive()) {
      this.message = 'กำลังคืนค่าหน้าเดิม…'
      try {
        await this.loadSupport(supportState.selectedContractId, supportState.returnPath)
      } catch (error) { this.fail('ไม่สามารถคืนค่าหน้าเดิมได้ กรุณาเปิดจากหน้าผู้ดูแลใหม่') }
      return
    }
    const nonce = (this.$route.hash || '').replace(/^#nonce=/, '')
    let adminOrigin
    try {
      if (!process.env.VUE_APP_ADMIN_URL || !window.opener || !/^[a-f0-9]{32}$/.test(nonce)) throw new Error('invalid_entry')
      const admin = new URL(process.env.VUE_APP_ADMIN_URL)
      if (!['https:', 'http:'].includes(admin.protocol)) throw new Error('invalid_origin')
      if (process.env.NODE_ENV === 'production' && admin.protocol !== 'https:') throw new Error('https_required')
      adminOrigin = admin.origin
    } catch (error) { this.fail('กรุณาเปิดหน้านี้จากหน้าผู้ดูแล และตรวจการตั้งค่า VUE_APP_ADMIN_URL'); return }
    const opener = window.opener
    let accepted = false
    this.supportListener = async event => {
      if (accepted || event.origin !== adminOrigin || event.source !== opener || !event.data || event.data.type !== 'wel-support-grant' || event.data.nonce !== nonce) return
      accepted = true
      this.cleanupHandshake()
      try {
        startSupport(event.data)
        window.history.replaceState(window.history.state, '', window.location.pathname)
        opener.postMessage({ type: 'wel-support-accepted', nonce }, adminOrigin)
        window.opener = null
        this.message = 'กำลังโหลดข้อมูลนักศึกษา…'
        await this.loadSupport(event.data.contractId, '/')
      } catch (error) { this.fail('ไม่สามารถเปิดข้อมูลได้ กรุณาตรวจสิทธิ์และสัญญาของนักศึกษา แล้วเปิดจากหน้าผู้ดูแลใหม่') }
    }
    window.addEventListener('message', this.supportListener)
    this.supportTimer = window.setTimeout(() => this.fail('ไม่ได้รับสิทธิ์ กรุณาเปิดจากหน้าผู้ดูแลใหม่'), 45000)
    opener.postMessage({ type: 'wel-support-ready', nonce }, adminOrigin)
  },
  beforeDestroy () { this.cleanup() },
  methods: {
    async loadSupport (contractId, returnPath) {
      const [userResult, contractResult] = await Promise.all([
        supportQuery(GET_CURRENT_USER),
        supportQuery(GET_CONTRACTS_BY_CURRENT_USER)
      ])
      if (!supportActive()) throw new Error('expired')
      const contracts = contractResult.data.getContractsByCurrentUser
      const selected = contractId ? contracts.find(item => item.id === contractId) : contracts[0]
      if (!selected || !selected.loan) throw new Error('missing_contract')
      selectSupportContract(selected.id)
      this.$store.commit('setCurrentUser', userResult.data.getCurrentUser)
      this.$store.commit('Accounting/setContracts', contracts)
      this.$store.commit('Accounting/setContract', selected)
      markSupportReady()
      window.clearTimeout(this.detailsTimer)
      await this.$router.replace(returnPath || '/')
    },
    cleanupHandshake () {
      window.clearTimeout(this.supportTimer)
      if (this.supportListener) window.removeEventListener('message', this.supportListener)
    },
    cleanup () {
      this.cleanupHandshake()
      window.clearTimeout(this.detailsTimer)
    },
    fail (message) {
      this.cleanup()
      this.failed = true
      this.showDetails = true
      endSupport(message)
      this.message = message
    }
  }
}
</script>

<style scoped>
.support-entry {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
  background: #f5f7fa;
  color: #526174;
}
.support-entry__loading { min-height: 84px; text-align: center; }
.support-entry__loading p { margin-top: 18px; font-size: 13px; }
.support-entry__spinner {
  display: block;
  width: 34px;
  height: 34px;
  margin: 0 auto;
  border: 3px solid #dce3eb;
  border-top-color: #49647e;
  border-radius: 50%;
  animation: support-spin .75s linear infinite;
}
.support-entry__error {
  width: 100%;
  max-width: 460px;
  padding: 36px 30px;
  box-sizing: border-box;
  border: 1px solid #e7ebf0;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 12px 40px rgba(28, 46, 68, .06);
  text-align: center;
}
.support-entry__error .feather-icon { color: #7b8998; }
.support-entry__error h1 { margin: 18px 0 12px; color: #273548; font-size: 23px; font-weight: 600; }
.support-entry__error p { line-height: 1.8; }
@keyframes support-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .support-entry__spinner { animation-duration: 1.5s; } }
@media (max-width: 480px) {
  .support-entry { padding: 16px; }
  .support-entry__error { padding: 30px 20px; }
}
</style>
